import { PaymentMethod } from '../../../constants/order'
import { Roles } from '../../../constants/roles'
import { objectReduce } from '../../../utils/objectReduce'
import { onlyNumbers } from '../../../utils/onlyNumbers'

export const generateSignUpPurchasePayload = (signUpState, paymentMethod) => {
  const { step1, step2, step3, step4 } = signUpState

  const product = step3.productSelected
  const cellphone = onlyNumbers(step2.cellphone)
  const expirationDate = step4.creditCard.cardExpiringDate
  const expMonth = Number(expirationDate.substring(0, 2))
  const expYear = Number(expirationDate.substring(3, 5))

  const user = {
    name: step2.name,
    lastname: step2.lastname,
    cellphone,
    document: onlyNumbers(step1.cnpj),
    email: step2.email,
    password: step2.password,
    role: Roles.ADMIN_USER
  }

  const company = {
    corporateName: step1.razaoSocial,
    cnpj: onlyNumbers(step1.cnpj),
    urlWebsite: step1.url,
    status: 'ACTIVE',
    address: `${step1.address}, ${step1.number}${
      step1.complement ? ` - ${step1.complement}` : ''
    }`,
    zipCode: step1.zipCode,
    city: step1.city,
    state: step1.state,
    country: step1.country
  }

  const brand = {
    brandName: step1.brandName,
    urlWebsite: step1.url,
    status: 'ACTIVE'
  }

  const order = {
    items: [
      {
        quantity: 1,
        code: product.id
      }
    ],
    payments: [
      {
        paymentMethod,
        ...(paymentMethod === PaymentMethod.CREDIT_CARD && {
          creditCard: {
            recurrence: false,
            installments: Number(step4.creditCard.installments),
            statementDescriptor: 'ECBR',
            card: {
              number: onlyNumbers(step4.creditCard.cardNumber),
              holderName: step4.creditCard.cardHolderName,
              expMonth,
              expYear,
              cvv: step4.creditCard.cardCvv
            }
          }
        })
      }
    ],
    ...(step4.coupon && { coupon: step4.coupon })
  }

  return { user, company, brand, order }
}

export const generateSignupPurchaseNewBrandPayload = (
  signUpState,
  paymentMethod,
  user
) => {
  // payload example:
  // {
  //   "company": {
  //     "id": 1
  //   },
  //   "brand": {
  //     "brandName": "Nome da Marca",
  //     "urlWebsite": "www.nomedamarca.com.br",
  //     "status": "ACTIVE"
  //   },
  //   "order": {
  //     "items": [
  //       {
  //         "quantity": 1,
  //         "code": 1
  //       }
  //     ],
  //     "payments": [
  //       {
  //         "paymentMethod": "CREDIT_CARD",
  //         "creditCard": {
  //           "recurrence": false,
  //           "installments": 1,
  //           "statementDescriptor": "ECBR",
  //           "card": {
  //             "number": "1234567890123456",
  //             "holderName": "João Silva",
  //             "expMonth": 12,
  //             "expYear": 2020,
  //             "cvv": 123
  //           }
  //         }
  //       }
  //     ],
  //     "coupon": "12345678901234567890"
  //   }
  // }

  const { step1, step3, step4 } = signUpState
  const [company] = user.companies || []

  const product = step3.productSelected
  const expirationDate = step4.creditCard.cardExpiringDate
  const expMonth = Number(expirationDate.substring(0, 2))
  const expYear = Number(expirationDate.substring(3, 5))

  const companyId = company?.id

  const brand = {
    brandName: step1.brandName,
    urlWebsite: step1.url,
    status: 'ACTIVE'
  }

  const order = {
    items: [
      {
        quantity: 1,
        code: product.id
      }
    ],
    payments: [
      {
        paymentMethod,
        ...(paymentMethod === PaymentMethod.CREDIT_CARD && {
          creditCard: {
            recurrence: false,
            installments: Number(step4.creditCard.installments),
            statementDescriptor: 'ECBR',
            card: {
              number: onlyNumbers(step4.creditCard.cardNumber),
              holderName: step4.creditCard.cardHolderName,
              expMonth,
              expYear,
              cvv: step4.creditCard.cardCvv
            }
          }
        })
      }
    ],
    ...(step4.coupon && { coupon: step4.coupon })
  }

  return { companyId, brand, order }
}

export const verifyError = (errors, field, errorMessage) =>
  objectReduce(
    errors?.[0],
    (acc, curr, key) =>
      acc || (key.includes(field) && curr?.[0]?.includes(errorMessage)),
    false
  )

export const verifyErrAlreadyExists = (errors, field) =>
  verifyError(errors, field, 'already exists')

export const verifyErrIsUnique = (errors, field) =>
  verifyError(errors, field, 'is unique')

export const generateErrorMessage = (errors, errorMessage, signupPayload) => {
  const companyAlreadyExists = verifyErrAlreadyExists(errors, 'company.cnpj')
  const corporateNameAlreadyExists = verifyErrAlreadyExists(
    errors,
    'company.corporateName'
  )
  const urlWebsiteAlreadyExists = verifyErrAlreadyExists(
    errors,
    'company.urlWebsite'
  )
  const userEmailAlreadyExists = verifyErrAlreadyExists(errors, 'user.email')

  const userEmailIsUnique = verifyErrIsUnique(errors, 'user.email')

  errorMessage += '\n'

  if (companyAlreadyExists) {
    errorMessage += `- Já existe uma empresa cadastrada com o CNPJ "${signupPayload.company.cnpj}"\n`
  }

  if (corporateNameAlreadyExists) {
    errorMessage += `- Já existe uma empresa cadastrada com o nome fantasia "${signupPayload.company.corporateName}"\n`
  }

  if (urlWebsiteAlreadyExists) {
    errorMessage += `- Já existe uma empresa cadastrada com o site "${signupPayload.company.urlWebsite}"\n`
  }

  if (userEmailAlreadyExists || userEmailIsUnique) {
    errorMessage += `- Já existe um usuário cadastrado com o e-mail "${signupPayload.user.email}"\n`
  }

  if (errors?.[0]?.message) {
    errorMessage += `- ${errors[0].message}\n`
  }

  errorMessage +=
    '\nTente novamente, faça login ou entre em contato com o suporte.'
  return errorMessage
}
